import 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import i18n from '@/i18n';
import { TranslateResult } from 'vue-i18n';
import { ActivityItemStatus } from '@/services/UndergradGrades';

dayjs.locale('pt-br');

export enum ActivityMessageStatus {
  DONE = 'done',
  PENDING = 'pending',
  WAITING_GRADING = 'waitingGrading',
}

const statusMapper: { [key in ActivityItemStatus]: ActivityMessageStatus } = {
  [ActivityItemStatus.DONE]: ActivityMessageStatus.DONE,
  [ActivityItemStatus.DONE_DELAYED]: ActivityMessageStatus.DONE,
  [ActivityItemStatus.BLOCKED]: ActivityMessageStatus.PENDING,
  [ActivityItemStatus.PENDING]: ActivityMessageStatus.PENDING,
  [ActivityItemStatus.PENDING_DELAYED]: ActivityMessageStatus.PENDING,
  [ActivityItemStatus.WAITING]: ActivityMessageStatus.WAITING_GRADING,
  [ActivityItemStatus.WAITING_DELAYED]: ActivityMessageStatus.WAITING_GRADING,
};

export default class DueDateWarningHelper {
  static getActivityStatus(status: ActivityItemStatus): ActivityMessageStatus {
    return statusMapper[status];
  }

  static isPastDue(
    dueDate: Date | undefined,
    answeredAt: Date | undefined,
    status: ActivityItemStatus,
  ): boolean {
    if (!dueDate) return false;

    if (!answeredAt || statusMapper[status] === ActivityMessageStatus.PENDING)
      return dayjs(new Date()).isAfter(dueDate);

    return dayjs(answeredAt).isAfter(dueDate);
  }

  static getComplementaryMessage(status: ActivityMessageStatus): TranslateResult {
    const statusKey = status === ActivityMessageStatus.DONE ? 'done' : 'default';
    return i18n.t(`activityWarning.noDue.generic.${statusKey}`);
  }

  static getGenericMessage(
    status: ActivityMessageStatus,
    isPastDue: boolean,
    dueDate: Date,
  ): TranslateResult {
    if (!isPastDue && status === ActivityMessageStatus.PENDING) {
      const parsedDate = dayjs(dueDate);
      return i18n.t(`activityWarning.beforeDue.generic.pending`, {
        day: parsedDate.date(),
        month: parsedDate.format('MMMM'),
      });
    }
    const dueKey = isPastDue ? 'pastDue' : 'beforeDue';
    return i18n.t(`activityWarning.${dueKey}.generic.${status}`);
  }

  static getObjectiveWarningMessage(
    status: ActivityItemStatus,
    isPastDue: boolean,
    dueDate: Date | undefined,
  ): TranslateResult {
    const mappedStatus = this.getActivityStatus(status);
    if (!dueDate) return this.getComplementaryMessage(mappedStatus);

    return this.getGenericMessage(mappedStatus, isPastDue, dueDate);
  }

  static getTriviaWarningMessage(
    status: ActivityItemStatus,
    isPastDue: boolean,
    dueDate: Date | undefined,
  ): TranslateResult {
    const mappedStatus = this.getActivityStatus(status);
    if (!dueDate) return this.getComplementaryMessage(mappedStatus);

    if (!isPastDue && mappedStatus === ActivityMessageStatus.WAITING_GRADING) {
      return i18n.t(`activityWarning.beforeDue.trivia.waitingGrading`);
    }

    return this.getGenericMessage(mappedStatus, isPastDue, dueDate);
  }

  static getPracticeWarningMessage(
    status: ActivityItemStatus,
    isPastDue: boolean,
    dueDate: Date | undefined,
  ): TranslateResult {
    const mappedStatus = this.getActivityStatus(status);
    if (!dueDate) return this.getComplementaryMessage(mappedStatus);

    if (!isPastDue && mappedStatus === ActivityMessageStatus.WAITING_GRADING) {
      return i18n.t(`activityWarning.beforeDue.practice.waitingGrading`);
    }

    return this.getGenericMessage(mappedStatus, isPastDue, dueDate);
  }

  static getReviewListWarningMessage(
    status: ActivityItemStatus,
    isPastDue: boolean,
    dueDate: Date,
  ): TranslateResult {
    if (status === ActivityItemStatus.DONE) {
      const isRedoingWorthingGrade = new Date() < dueDate;

      const parsedDate = dayjs(dueDate);
      const variables = {
        day: parsedDate.date(),
        month: parsedDate.format('MMMM'),
      };

      return isRedoingWorthingGrade
        ? i18n.t('activityWarning.beforeDue.reviewList.done', variables)
        : i18n.t('activityWarning.pastDue.reviewList.done');
    }

    const messageStatus = this.getActivityStatus(status);
    return this.getGenericMessage(messageStatus, isPastDue, dueDate);
  }
}
